import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Clock,
  CheckSquare,
  Camera,
  ClipboardList,
  Grid3X3,
  ChevronRight,
  Printer,
  Mail,
} from 'lucide-react';
import { formatTimeDisplay } from '@peles/shared';
import type { ExpenseSummary } from '@peles/shared';
import { ClientFloorPlanCanvas } from '../components/floor-plan/ClientFloorPlanCanvas';
import { ClientGuestListView } from '../components/portal/ClientGuestListView';
import {
  useClientEventDetail,
  useClientExpenseSummary,
  useClientExpenseItems,
  useClientEventFloorPlans,
  useClientEventPhotos,
  useClientEventTimeline,
  useClientEventChecklist,
  useClientEventQuestionnaires,
} from '../hooks/usePortal';
import { ClientExpenseDocumentsModal } from '../components/portal/ClientExpenseDocumentsModal';
import { ClientExpenseTable } from '../components/portal/ClientExpenseTable';
import { QuestionnaireFillView } from '../components/questionnaires';
import { InviteRsvpSection } from '../components/invites/InviteRsvpSection';

type Tab =
  | 'overview'
  | 'expenses'
  | 'guests'
  | 'floor-plan'
  | 'timeline'
  | 'checklist'
  | 'photos'
  | 'questionnaires'
  | 'invites';

const TABS: { id: Tab; label: string; icon: typeof Calendar }[] = [
  { id: 'overview', label: 'Overview', icon: Calendar },
  { id: 'expenses', label: 'Expenses', icon: DollarSign },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'floor-plan', label: 'Floor Plan', icon: Grid3X3 },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'checklist', label: 'Checklist', icon: CheckSquare },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'questionnaires', label: 'Questionnaires', icon: ClipboardList },
  // Only shown when the client's permissions include canManageInvites.
  { id: 'invites', label: 'Invites', icon: Mail },
];

const STATUS_COLORS: Record<string, string> = {
  DONE: 'bg-green-100 text-green-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  BLOCKED: 'bg-red-100 text-red-700',
  TODO: 'bg-gray-100 text-gray-600',
};

function OverviewTab({ eventId }: { eventId: string }) {
  const { data: event, isLoading } = useClientEventDetail(eventId);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!event) return null;

  const startDate = new Date(event.startAt).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const endDate = event.endAt
    ? new Date(event.endAt).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white rounded-lg border p-4 space-y-3">
        <h3 className="font-semibold text-lg">Event Details</h3>
        {event.eventTypeName && (
          <div>
            <p className="text-xs text-gray-500">Type</p>
            <p className="font-medium">{event.eventTypeName}</p>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-500">Date</p>
          <p className="font-medium">{startDate}</p>
          {endDate && <p className="text-sm text-gray-600">through {endDate}</p>}
        </div>
        <div>
          <p className="text-xs text-gray-500">Status</p>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
            {event.status}
          </span>
        </div>
        {event.guestCount != null && (
          <div>
            <p className="text-xs text-gray-500">Expected Guests</p>
            <p className="font-medium">{event.guestCount}</p>
          </div>
        )}
      </div>

      {(event.venueName || event.venueAddress) && (
        <div className="bg-white rounded-lg border p-4 space-y-3">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <MapPin size={18} />
            Venue
          </h3>
          {event.venueName && <p className="font-medium">{event.venueName}</p>}
          {event.venueAddress && <p className="text-sm text-gray-600">{event.venueAddress}</p>}
          {event.venueCity && <p className="text-sm text-gray-600">{event.venueCity}</p>}
        </div>
      )}

      {event.notes && (
        <div className="bg-white rounded-lg border p-4 md:col-span-2">
          <h3 className="font-semibold text-lg mb-2">Notes</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{event.notes}</p>
        </div>
      )}
    </div>
  );
}

/** The client's budget tiles — 'Owed to Producer' only when money was fronted. */
function summaryTiles(summary: ExpenseSummary): {
  label: string;
  value: string;
  className?: string;
}[] {
  const tiles = [
    { label: 'Total', value: `$${summary.totalCost.toLocaleString()}` },
    { label: 'Paid', value: `$${summary.totalPaid.toLocaleString()}`, className: 'text-green-600' },
    {
      label: 'Open Balance',
      value: `$${summary.balance.toLocaleString()}`,
      className: 'text-red-600',
    },
  ];
  if (summary.producerReimbursementDue > 0.005) {
    tiles.push({
      label: 'Owed to Producer',
      value: `$${summary.producerReimbursementDue.toLocaleString()}`,
      className: 'text-amber-600',
    });
  }
  tiles.push({ label: 'Items', value: String(summary.itemCount) });
  return tiles;
}

function ExpensesTab({ eventId }: { eventId: string }) {
  const { data: summary, isLoading: summaryLoading } = useClientExpenseSummary(eventId);
  const { data: items, isLoading: itemsLoading } = useClientExpenseItems(eventId);
  const [docPreviewId, setDocPreviewId] = useState<string | null>(null);

  if (summaryLoading || itemsLoading) return <p className="text-gray-500 p-4">Loading...</p>;

  return (
    <div className="space-y-4">
      {docPreviewId && (
        <ClientExpenseDocumentsModal
          eventId={eventId}
          expenseItemId={docPreviewId}
          onClose={() => setDocPreviewId(null)}
        />
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summaryTiles(summary).map(({ label, value, className }) => (
            <div key={label} className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500">{label}</p>
              <p className={`text-xl font-bold ${className ?? ''}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <ClientExpenseTable
        items={items ?? []}
        onPreviewDocuments={(item) => setDocPreviewId(item.id)}
      />
    </div>
  );
}

function FloorPlanTab({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { data: plans, isLoading } = useClientEventFloorPlans(eventId);
  const { data: event } = useClientEventDetail(eventId);
  const canPrint = event?.permissions?.canPrint ?? false;
  const [openPlan, setOpenPlan] = useState<string | null>(null);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!plans || plans.length === 0)
    return <p className="text-gray-500 text-center py-8">No floor plans available.</p>;

  return (
    <div className="space-y-4">
      {plans.map((plan) => {
        const tables = plan.objects.filter((o) => o.tableNumber != null);
        const objects = plan.objects.filter((o) => o.tableNumber == null);
        const isOpen = openPlan === plan.id;

        return (
          <div key={plan.id} className="bg-white rounded-lg border overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() => setOpenPlan(isOpen ? null : plan.id)}
            >
              <div>
                <h3 className="font-semibold">{plan.name}</h3>
                {plan.roomName && <p className="text-sm text-gray-500">{plan.roomName}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {tables.length} tables · {objects.length} objects
                </span>
                <ChevronRight
                  size={18}
                  className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="border-t p-4">
                <ClientFloorPlanCanvas
                  plan={plan}
                  eventName={eventName}
                  eventDate={event?.startAt ?? null}
                  venueName={event?.venueName ?? null}
                  canPrint={canPrint}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TimelineTab({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { data: timelines, isLoading } = useClientEventTimeline(eventId);
  const { data: event } = useClientEventDetail(eventId);
  const canPrint = event?.permissions?.canPrint ?? false;

  // Entries arrive from the portal API with `{tag}` placeholders already
  // merged to the event's tag values — print them as displayed.
  const handlePrint = (timeline: NonNullable<typeof timelines>[number]) => {
    const esc = (s: string | null | undefined) =>
      (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const title = eventName ? `${eventName}: ${timeline.name}` : timeline.name;
    const sectionsHtml = timeline.sections
      .map(
        (section) =>
          `<h2>${esc(section.name)}</h2><table>` +
          section.entries
            .map(
              (entry) =>
                `<tr><td class="t">${esc(formatTimeDisplay(entry.time))}</td><td><strong>${esc(entry.title)}</strong>` +
                (entry.location ? `<div class="loc">${esc(entry.location)}</div>` : '') +
                (entry.notes ? `<div class="notes">${esc(entry.notes)}</div>` : '') +
                `</td></tr>`,
            )
            .join('') +
          `</table>`,
      )
      .join('');
    const printWin = window.open('', '_blank', 'width=900,height=800');
    if (!printWin) return;
    printWin.document.write(
      `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${esc(title)}</title>` +
        `<style>` +
        `@page{margin:15mm;}` +
        `body{margin:0;font-family:sans-serif;color:#111;font-size:13px;}` +
        `h1{font-size:18px;margin:0 0 2px 0;}` +
        `.desc{color:#555;font-size:12px;margin:0 0 12px 0;}` +
        `h2{font-size:12px;text-transform:uppercase;letter-spacing:0.05em;color:#555;border-bottom:1px solid #ddd;padding-bottom:4px;margin:16px 0 6px 0;}` +
        `table{width:100%;border-collapse:collapse;}` +
        `td{padding:6px 8px;border-bottom:1px solid #f0f0f0;vertical-align:top;}` +
        `td.t{width:70px;font-family:monospace;color:#555;white-space:nowrap;}` +
        `.loc{color:#555;font-size:12px;}` +
        `.notes{color:#444;font-size:12px;margin-top:2px;}` +
        `</style></head><body>` +
        `<h1>${esc(title)}</h1>` +
        (timeline.description ? `<p class="desc">${esc(timeline.description)}</p>` : '') +
        sectionsHtml +
        `<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>` +
        `</body></html>`,
    );
    printWin.document.close();
  };

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!timelines || timelines.length === 0)
    return <p className="text-gray-500 text-center py-8">No timeline available.</p>;

  return (
    <div className="space-y-4">
      {timelines.map((timeline) => (
        <div key={timeline.id} className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold">{timeline.name}</h3>
              {timeline.description && (
                <p className="text-sm text-gray-500 mt-1">{timeline.description}</p>
              )}
            </div>
            {canPrint && (
              <button
                onClick={() => handlePrint(timeline)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-gray-700 shrink-0"
                data-testid={`print-timeline-${timeline.id}`}
              >
                <Printer size={15} />
                Print
              </button>
            )}
          </div>
          {timeline.sections.map((section) => (
            <div key={section.id}>
              <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {section.name}
              </div>
              <div className="divide-y">
                {section.entries.map((entry) => (
                  <div key={entry.id} className="p-4 flex gap-4">
                    {entry.time && (
                      <div className="text-sm font-mono text-gray-500 shrink-0 w-16">
                        {formatTimeDisplay(entry.time)}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{entry.title}</p>
                      {entry.location && (
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin size={12} />
                          {entry.location}
                        </p>
                      )}
                      {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ChecklistTab({ eventId }: { eventId: string }) {
  const { data: checklists, isLoading } = useClientEventChecklist(eventId);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!checklists || checklists.length === 0)
    return <p className="text-gray-500 text-center py-8">No checklist available.</p>;

  return (
    <div className="space-y-4">
      {checklists.map((checklist) => (
        <div key={checklist.id} className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">{checklist.name}</h3>
            {checklist.description && (
              <p className="text-sm text-gray-500 mt-1">{checklist.description}</p>
            )}
          </div>
          {checklist.sections.map((section) => (
            <div key={section.id}>
              <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                {section.name}
              </div>
              <div className="divide-y">
                {section.items.map((item) => (
                  <div key={item.id} className="p-3 flex items-start gap-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 mt-0.5 ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}
                    >
                      {item.status}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.title}</p>
                      {item.dueDate && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Due:{' '}
                          {(() => {
                            const d = new Date(item.dueDate);
                            return new Date(
                              d.getUTCFullYear(),
                              d.getUTCMonth(),
                              d.getUTCDate(),
                            ).toLocaleDateString();
                          })()}
                        </p>
                      )}
                      {item.notes && <p className="text-xs text-gray-600 mt-0.5">{item.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PhotosTab({ eventId }: { eventId: string }) {
  const { data: photos, isLoading } = useClientEventPhotos(eventId);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!photos || photos.length === 0)
    return <p className="text-gray-500 text-center py-8">No photos available.</p>;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">{photos.length} photo(s) attached to this event.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <div key={photo.id} className="bg-white rounded-lg border overflow-hidden">
            <div className="aspect-square bg-gray-100 flex items-center justify-center">
              <Camera size={32} className="text-gray-400" />
            </div>
            <div className="p-2">
              <p className="text-xs text-gray-600 truncate">{photo.fileName}</p>
              {photo.caption && <p className="text-xs text-gray-500 truncate">{photo.caption}</p>}
              {photo.takenAt && (
                <p className="text-xs text-gray-400">
                  {new Date(photo.takenAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const QUESTIONNAIRE_STATUS_COLORS: Record<string, string> = {
  SENT: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  SUBMITTED: 'bg-green-100 text-green-700',
  REVIEWED: 'bg-purple-100 text-purple-700',
};

const QUESTIONNAIRE_STATUS_LABELS: Record<string, string> = {
  SENT: 'Not Started',
  IN_PROGRESS: 'In Progress',
  SUBMITTED: 'Submitted',
  REVIEWED: 'Reviewed',
};

function QuestionnairesTab({
  eventId,
  initialQuestionnaireId,
}: {
  eventId: string;
  initialQuestionnaireId?: string | null;
}) {
  const { data: questionnaires, isLoading } = useClientEventQuestionnaires(eventId);
  const [openId, setOpenId] = useState<string | null>(initialQuestionnaireId ?? null);

  // A single questionnaire opens directly — no list to pick from.
  useEffect(() => {
    if (questionnaires?.length === 1) setOpenId(questionnaires[0].id);
  }, [questionnaires]);

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!questionnaires || questionnaires.length === 0)
    return <p className="text-gray-500 text-center py-8">No questionnaires available.</p>;

  return (
    <div className="space-y-4">
      {questionnaires.map((questionnaire) => {
        const isOpen = openId === questionnaire.id;
        return (
          <div key={questionnaire.id} className="bg-white rounded-lg border overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
              onClick={() => setOpenId(isOpen ? null : questionnaire.id)}
              data-testid={`questionnaire-row-${questionnaire.id}`}
            >
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{questionnaire.title}</h3>
                {questionnaire.description && (
                  <p className="text-sm text-gray-500 truncate">{questionnaire.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {questionnaire.answeredCount}/{questionnaire.questionCount} answered
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded font-medium ${
                    QUESTIONNAIRE_STATUS_COLORS[questionnaire.status] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {QUESTIONNAIRE_STATUS_LABELS[questionnaire.status] || questionnaire.status}
                </span>
                <ChevronRight
                  size={18}
                  className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </div>
            </button>

            {isOpen && (
              <div className="border-t p-4 bg-gray-50">
                <QuestionnaireFillView token={questionnaire.accessToken} variant="embedded" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ClientEventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  // Deep-links (e.g. the public questionnaire redirect) select the initial tab
  // via ?tab=…&questionnaire=…
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    TABS.some((t) => t.id === tabParam) ? (tabParam as Tab) : 'overview',
  );
  const { data: event, isLoading } = useClientEventDetail(eventId!);

  if (isLoading) return <p className="p-4 text-gray-500">Loading event...</p>;
  if (!event) return <p className="p-4 text-gray-500">Event not found.</p>;

  const canManageInvites = event.permissions?.canManageInvites ?? false;
  const canManageGuests = event.permissions?.canManageGuests ?? false;
  const visibleTabs = TABS.filter((tab) => tab.id !== 'invites' || canManageInvites);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Link
          to="/client"
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mt-1"
        >
          <ArrowLeft size={16} />
          My Events
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold">{event.name}</h1>
        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
          {event.eventTypeName && <span>{event.eventTypeName}</span>}
          {event.venueName && (
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {event.venueName}
            </span>
          )}
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{event.status}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <OverviewTab eventId={eventId!} />}
        {activeTab === 'expenses' && <ExpensesTab eventId={eventId!} />}
        {activeTab === 'guests' && (
          <ClientGuestListView eventId={eventId!} canManageGuests={canManageGuests} />
        )}
        {activeTab === 'floor-plan' && <FloorPlanTab eventId={eventId!} eventName={event.name} />}
        {activeTab === 'timeline' && <TimelineTab eventId={eventId!} eventName={event.name} />}
        {activeTab === 'checklist' && <ChecklistTab eventId={eventId!} />}
        {activeTab === 'photos' && <PhotosTab eventId={eventId!} />}
        {activeTab === 'questionnaires' && (
          <QuestionnairesTab
            eventId={eventId!}
            initialQuestionnaireId={searchParams.get('questionnaire')}
          />
        )}
        {activeTab === 'invites' && canManageInvites && (
          <InviteRsvpSection eventId={eventId!} clientMode />
        )}
      </div>
    </div>
  );
}
